// Backend data handler for workflow storage and retrieval
// Handles encrypted workflow data with fallback mechanisms

import logger from '../utils/logger';
import { workflowStorageService } from './workflowStorageService';
import secureWorkflowDataService from './secureWorkflowDataService';
import { decryptFromTransmission } from '../utils/clientEncryption';

// Types for backend data operations
export interface BackendDataResponse<T = any> {
    success: boolean;
    data?: T;
    error?: string;
    fallbackUsed?: boolean;
}

export interface EncryptedDataReference {
    id: string;
    type: 'workflow' | 'template' | 'salt';
    isSecureReference: boolean;
    displayValue: string; // For UI display like {{workflow_step1_...}}
}

class BackendDataHandler {
    private readonly REFERENCE_PREFIX = 'workflow_';
    private readonly REFERENCE_PATTERN = /^workflow_step\d+_\d+_[a-zA-Z0-9]+$/;

    /**
     * Check if a value is a secure workflow reference
     */
    isSecureReference(value: string): boolean {
        // Check for {{...}} format first
        if (value.startsWith('{{') && value.endsWith('}}')) {
            logger.log('✅ Detected secure reference ({{}} format):', value);
            return true;
        }

        // CRITICAL FIX: Check for environment-prefixed patterns (dev_wf_, prod_wf_, loc_wf_, dev_tpl_, prod_tpl_, loc_tpl_, dev_salt_, prod_salt_, loc_salt_)
        if (value.match(/^(dev_|prod_|loc_)(wf_|tpl_|salt_)/)) {
            logger.log('✅ Detected secure reference (environment-prefixed):', value);
            return true;
        }

        // Check for legacy workflow_ prefix pattern
        if (value.startsWith('workflow_')) {
            logger.log('✅ Detected secure reference (workflow_ prefix):', value);
            return true;
        }

        // Check for step pattern (without any prefix)
        if (value.match(/^step\d+_\d+_[a-zA-Z0-9]+$/)) {
            logger.log('✅ Detected secure reference (step pattern):', value);
            return true;
        }

        // Check for salt_ prefix pattern (direct salt references)
        if (value.startsWith('salt_')) {
            logger.log('✅ Detected secure reference (salt_ prefix):', value);
            return true;
        }

        // Check for template_ prefix pattern (direct template references)
        if (value.startsWith('template_')) {
            logger.log('✅ Detected secure reference (template_ prefix):', value);
            return true;
        }

        // Check original pattern and secure service
        const isReference = this.REFERENCE_PATTERN.test(value) || secureWorkflowDataService.isValidReference(value);
        if (isReference) {
            logger.log('✅ Detected secure reference (pattern match):', value);
        }
        return isReference;
    }

    /**
     * Format reference for UI display
     */
    formatReferenceForDisplay(reference: string): string {
        if (this.isSecureReference(reference)) {
            return `{{${reference}}}`;
        }
        return reference;
    }

    /**
     * Extract reference ID from display format
     */
    extractReferenceFromDisplay(displayValue: string): string {
        const match = displayValue.match(/^\{\{(.+)\}\}$/);
        return match ? match[1] : displayValue;
    }

    /**
     * Store encrypted workflow data in backend
     */
    async storeEncodedData(
        data: any,
        type: 'workflow' | 'template' | 'salt' = 'workflow',
        id?: string
    ): Promise<BackendDataResponse<{ id: string; reference: string }>> {
        try {
            logger.log('📦 Storing encoded data to backend:', { type, id: id?.substring(0, 20) + '...' });

            let response;
            const timestamp = Date.now();
            const generatedId = id || `${type}_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;

            switch (type) {
                case 'workflow':
                    // CRITICAL FIX: Extract sessionEncrypted and sessionId from data if present
                    // These need to be at the TOP LEVEL for backend session decryption to work
                    const workflowPayload: any = {
                        id: generatedId,
                        name: `Workflow Data ${timestamp}`,
                        description: 'Encrypted workflow function data',
                        category: 'utility',
                        difficulty: 'intermediate',
                        estimatedTime: 5,
                        tags: ['workflow', 'encrypted'],
                        created: timestamp,
                        functionData: typeof data === 'string' ? data : JSON.stringify(data)
                        // templateData will be set below after extracting session encryption metadata
                    };

                    // CRITICAL: If data has sessionEncrypted flag, pass through all encryption data
                    // FunctionBuilder now sends encrypted/iv at TOP LEVEL (not nested)
                    if (data && typeof data === 'object' && data.sessionEncrypted) {
                        workflowPayload.sessionEncrypted = data.sessionEncrypted;
                        workflowPayload.sessionId = data.sessionId;

                        // CRITICAL FIX: encrypted and iv are already at top level from FunctionBuilder
                        // Just pass them through directly
                        workflowPayload.encrypted = data.encrypted;
                        workflowPayload.iv = data.iv;

                        // CRITICAL FIX: Create templateData WITHOUT session encryption metadata
                        // This prevents double-nesting of session encryption in storage
                        const { sessionEncrypted, sessionId, encrypted, iv, ...cleanData } = data;
                        workflowPayload.templateData = cleanData;

                        logger.log('🔐 STORAGE FIX: Passing through session encryption data:', {
                            sessionEncrypted: workflowPayload.sessionEncrypted,
                            sessionId: workflowPayload.sessionId?.substring(0, 20) + '...',
                            hasEncrypted: !!workflowPayload.encrypted,
                            hasIv: !!workflowPayload.iv,
                            encryptedLength: workflowPayload.encrypted?.length,
                            ivLength: workflowPayload.iv?.length
                        });
                    } else {
                        // No session encryption, use data as-is for templateData
                        workflowPayload.templateData = data;
                    }

                    response = await workflowStorageService.storeWorkflow(workflowPayload, generatedId);
                    break;

                case 'template':
                    response = await workflowStorageService.storeTemplate({
                        id: generatedId,
                        contract: 'workflow',
                        contractAddress: '',
                        functionName: 'workflowData',
                        functionData: typeof data === 'string' ? data : JSON.stringify(data),
                        inputs: [],
                        timestamp,
                        description: 'Encrypted template data'
                    });
                    break;

                case 'salt':
                    response = await workflowStorageService.storeSalt({
                        salt: typeof data === 'string' ? data : JSON.stringify(data),
                        metadata: {
                            generated: timestamp,
                            algorithm: 'workflow',
                            purpose: 'workflow_encryption'
                        }
                    });
                    break;

                default:
                    throw new Error(`Unsupported data type: ${type}`);
            }

            if (response.success) {
                const reference = response.id || generatedId;
                logger.log('✅ Data stored successfully with reference:', reference);

                return {
                    success: true,
                    data: {
                        id: reference,
                        reference: reference
                    }
                };
            } else {
                throw new Error(response.error || 'Storage failed');
            }

        } catch (error) {
            logger.error('❌ Failed to store encoded data:', error);

            // Fallback to sessionStorage
            try {
                const fallbackId = id || `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                const fallbackData = {
                    id: fallbackId,
                    type,
                    data,
                    timestamp: Date.now(),
                    source: 'fallback'
                };

                sessionStorage.setItem(`backend_fallback_${fallbackId}`, JSON.stringify(fallbackData));
                logger.log('💾 Data stored in sessionStorage fallback:', fallbackId);

                return {
                    success: true,
                    data: {
                        id: fallbackId,
                        reference: fallbackId
                    },
                    fallbackUsed: true
                };
            } catch (fallbackError) {
                logger.error('❌ Fallback storage also failed:', fallbackError);
                return {
                    success: false,
                    error: `Backend storage failed: ${error instanceof Error ? error.message : String(error)}`
                };
            }
        }
    }

    /**
     * Retrieve and decrypt workflow data from backend
     */
    async retrieveEncodedData(
        id: string,
        type: 'workflow' | 'template' | 'salt' = 'workflow'
    ): Promise<BackendDataResponse<any>> {
        try {
            logger.log('📥 Retrieving encoded data from backend:', { id: id.substring(0, 20) + '...', type });

            // Check if this is a secure reference that needs special handling
            if (this.isSecureReference(id)) {
                logger.log('🔐 Detected secure reference, attempting retrieval...');
            }

            let response;
            switch (type) {
                case 'workflow':
                    response = await workflowStorageService.retrieveWorkflow(id);
                    break;

                case 'template':
                    response = await workflowStorageService.retrieveTemplate(id);
                    break;

                case 'salt':
                    response = await workflowStorageService.retrieveSalt(id);
                    break;

                default:
                    throw new Error(`Unsupported data type: ${type}`);
            }

            if (response.success && response.data) {
                logger.log('✅ Data retrieved successfully from backend');

                // CRITICAL DEBUG: Log the actual response structure
                logger.log('🔍 Response data type:', typeof response.data);
                logger.log('🔍 Response data keys:', Object.keys(response.data));
                logger.log('🔍 Response data preview:', JSON.stringify(response.data).substring(0, 200) + '...');

                // CRITICAL FIX: Handle tier1/tier2 structure from backend
                const responseData = response.data as any;

                // CROSS-SESSION FIX: Check if response is session-encrypted
                if (responseData.sessionEncrypted && responseData.encrypted) {
                    logger.log('🔓 CROSS-SESSION FIX: Decrypting session-encrypted response');

                    try {
                        const { decryptFromSession } = await import('../utils/sessionEncryption');

                        const encryptedData = {
                            encrypted: responseData.encrypted,
                            iv: responseData.iv,
                            sessionId: responseData.sessionId
                        };

                        const decrypted = await decryptFromSession(encryptedData);
                        const parsed = JSON.parse(decrypted);

                        logger.log('✅ CROSS-SESSION FIX: Session decryption successful');

                        // DIAGNOSTIC: Check what we got from session decryption
                        logger.log('🔍 DIAGNOSTIC: Parsed data type:', typeof parsed);
                        logger.log('🔍 DIAGNOSTIC: Parsed data keys:', Object.keys(parsed));

                        // Extract function data - may be nested JSON or direct hex
                        let functionData = parsed.functionData || parsed.templateData || parsed;

                        logger.log('🔍 DIAGNOSTIC: functionData type:', typeof functionData);
                        logger.log('🔍 DIAGNOSTIC: functionData exists:', !!functionData);

                        // CRITICAL FIX: Handle double-nested JSON from storage
                        // After session decryption, functionData might be a JSON string containing the original payload
                        if (typeof functionData === 'string') {
                            logger.log('🔍 DIAGNOSTIC: functionData length:', functionData.length);
                            logger.log('🔍 DIAGNOSTIC: functionData starts with 0x:', functionData.startsWith('0x'));

                            // If it's hex data, we're done
                            if (functionData.startsWith('0x')) {
                                logger.log('✅ functionData is already hex:', functionData.substring(0, 20) + '...');
                                return {
                                    success: true,
                                    data: functionData
                                };
                            }

                            logger.log('🔍 DIAGNOSTIC: functionData preview:', functionData.substring(0, 50) + '...');

                            // Check if functionData is a JSON string (double-nested)
                            if (functionData.startsWith('{')) {
                                try {
                                    logger.log('🔧 DOUBLE-NESTED FIX: Detected JSON string in functionData, parsing...');
                                    const innerData = JSON.parse(functionData);
                                    logger.log('🔍 DOUBLE-NESTED FIX: Inner data keys:', Object.keys(innerData));

                                    // DIAGNOSTIC: Log all field types and previews
                                    logger.log('🔍 DOUBLE-NESTED FIX: Field analysis:');
                                    Object.keys(innerData).forEach(key => {
                                        const value = innerData[key];
                                        const valueType = typeof value;
                                        let preview = '';

                                        if (valueType === 'string') {
                                            preview = value.substring(0, 50) + (value.length > 50 ? '...' : '');
                                        } else if (valueType === 'object' && value !== null) {
                                            preview = `{${Object.keys(value).join(', ')}}`;
                                        } else {
                                            preview = String(value);
                                        }

                                        logger.log(`  - ${key}: ${valueType} = ${preview}`);
                                    });

                                    // Try to find hex data in multiple fields
                                    let hexFound = false;

                                    // Check 1: innerData.functionData
                                    if (innerData.functionData) {
                                        // Check if it's an encrypted object that needs decryption
                                        if (typeof innerData.functionData === 'object' && innerData.functionData.encrypted && innerData.functionData.iv) {
                                            logger.log('🔐 DOUBLE-NESTED FIX: functionData is encrypted object, attempting decryption...');
                                            try {
                                                const { decryptFromSession } = await import('../utils/sessionEncryption');
                                                const decryptedFunctionData = await decryptFromSession(innerData.functionData);

                                                if (decryptedFunctionData && decryptedFunctionData.startsWith('0x')) {
                                                    functionData = decryptedFunctionData;
                                                    logger.log('✅ DOUBLE-NESTED FIX: Decrypted and extracted hex from functionData:', functionData.substring(0, 30) + '...');
                                                    hexFound = true;
                                                } else {
                                                    logger.warn('⚠️ DOUBLE-NESTED FIX: Decrypted functionData is not hex:', decryptedFunctionData?.substring(0, 50) + '...');
                                                }
                                            } catch (decryptError) {
                                                logger.error('❌ DOUBLE-NESTED FIX: Failed to decrypt functionData:', decryptError);
                                            }
                                        }
                                        // Check if it's already a hex string
                                        else if (typeof innerData.functionData === 'string') {
                                            if (innerData.functionData.startsWith('0x')) {
                                                functionData = innerData.functionData;
                                                logger.log('✅ DOUBLE-NESTED FIX: Extracted hex from nested functionData:', functionData.substring(0, 30) + '...');
                                                hexFound = true;
                                            } else {
                                                logger.log('⚠️ DOUBLE-NESTED FIX: Nested functionData exists but is not hex:', innerData.functionData.substring(0, 50) + '...');
                                            }
                                        }
                                    }

                                    // Check 2: innerData.inputs (might be encrypted or direct hex)
                                    if (!hexFound && innerData.inputs) {
                                        logger.log('🔍 DOUBLE-NESTED FIX: Checking inputs field...');

                                        if (typeof innerData.inputs === 'string') {
                                            if (innerData.inputs.startsWith('0x')) {
                                                functionData = innerData.inputs;
                                                logger.log('✅ DOUBLE-NESTED FIX: Extracted hex from inputs field:', functionData.substring(0, 30) + '...');
                                                hexFound = true;
                                            } else if (innerData.inputs.startsWith('{')) {
                                                // inputs might be encrypted JSON
                                                try {
                                                    const inputsData = JSON.parse(innerData.inputs);
                                                    logger.log('🔍 DOUBLE-NESTED FIX: inputs is JSON, keys:', Object.keys(inputsData));

                                                    // Check if it's an encrypted blob
                                                    if (inputsData.encrypted && inputsData.iv) {
                                                        logger.log('🔐 DOUBLE-NESTED FIX: inputs contains encrypted data, attempting decryption...');
                                                        // This would need session decryption - flag for now
                                                        logger.warn('⚠️ DOUBLE-NESTED FIX: inputs field requires decryption (not implemented yet)');
                                                    }
                                                } catch (e) {
                                                    logger.warn('⚠️ DOUBLE-NESTED FIX: inputs is not valid JSON');
                                                }
                                            }
                                        } else if (Array.isArray(innerData.inputs)) {
                                            logger.log('🔍 DOUBLE-NESTED FIX: inputs is an array, checking first element...');
                                            if (innerData.inputs.length > 0 && typeof innerData.inputs[0] === 'string' && innerData.inputs[0].startsWith('0x')) {
                                                functionData = innerData.inputs[0];
                                                logger.log('✅ DOUBLE-NESTED FIX: Extracted hex from inputs array:', functionData.substring(0, 30) + '...');
                                                hexFound = true;
                                            }
                                        }
                                    }

                                    // Check 3: innerData.metadata
                                    if (!hexFound && innerData.metadata && typeof innerData.metadata === 'object') {
                                        logger.log('🔍 DOUBLE-NESTED FIX: Checking metadata field...');
                                        if (innerData.metadata.functionData && typeof innerData.metadata.functionData === 'string' && innerData.metadata.functionData.startsWith('0x')) {
                                            functionData = innerData.metadata.functionData;
                                            logger.log('✅ DOUBLE-NESTED FIX: Extracted hex from metadata.functionData:', functionData.substring(0, 30) + '...');
                                            hexFound = true;
                                        }
                                    }

                                    if (!hexFound) {
                                        logger.error('❌ DOUBLE-NESTED FIX: Could not find hex data in any expected field');
                                    }
                                } catch (parseError) {
                                    logger.warn('⚠️ DOUBLE-NESTED FIX: Failed to parse nested JSON:', parseError);
                                    // Keep original functionData value
                                }
                            }
                        } else if (typeof functionData === 'object' && functionData !== null) {
                            logger.log('🔍 DIAGNOSTIC: functionData is object with keys:', Object.keys(functionData));
                        } else {
                            logger.log('🔍 DIAGNOSTIC: functionData value:', functionData);
                        }

                        return {
                            success: true,
                            data: functionData
                        };
                    } catch (sessionError) {
                        logger.error('❌ CROSS-SESSION FIX: Session decryption failed:', sessionError);
                        return {
                            success: false,
                            error: `Session decryption failed: ${sessionError instanceof Error ? sessionError.message : String(sessionError)}`
                        };
                    }
                }

                logger.log('🔍 Checking for tier1/tier2 structure:', {
                    hasTier1: !!responseData.tier1,
                    hasTier2: !!responseData.tier2,
                    hasEncryptedTier2: !!responseData.encryptedTier2,
                    requiresDecryption: !!responseData.requiresDecryption,
                    tier1Keys: responseData.tier1 ? Object.keys(responseData.tier1) : [],
                    tier2Keys: responseData.tier2 ? Object.keys(responseData.tier2) : [],
                    encryptedTier2Keys: responseData.encryptedTier2 ? Object.keys(responseData.encryptedTier2) : []
                });

                // CRITICAL FIX: Backend returns encryptedTier2 field, not tier2
                // Check for both formats: {tier1, tier2} and {encryptedTier2, requiresDecryption}
                const tier2Data = responseData.tier2 || responseData.encryptedTier2;

                if (tier2Data && (responseData.tier1 || responseData.requiresDecryption)) {
                    logger.log('🔐 Detected tier1/tier2 structure, decrypting tier2...');
                    logger.log('🔍 Tier2 structure:', {
                        hasEncrypted: !!tier2Data.encrypted,
                        hasIv: !!tier2Data.iv,
                        hasAuthTag: !!tier2Data.authTag,
                        encryptedLength: tier2Data.encrypted?.length,
                        ivLength: tier2Data.iv?.length,
                        authTagLength: tier2Data.authTag?.length
                    });

                    try {
                        logger.log('🔓 Attempting tier2 decryption with client encryption key...');

                        // Decrypt tier2 data using client encryption key
                        const decryptedTier2 = await decryptFromTransmission(tier2Data);
                        logger.log('✅ Tier2 decrypted successfully, length:', decryptedTier2.length);
                        logger.log('🔍 Decrypted data preview:', decryptedTier2.substring(0, 100) + '...');

                        // Parse the decrypted tier2 data
                        const decryptedData = JSON.parse(decryptedTier2);
                        logger.log('✅ Tier2 parsed successfully');
                        logger.log('🔍 Tier2 data keys:', Object.keys(decryptedData));

                        // Extract function data from tier2
                        const functionData = decryptedData.functionData || decryptedData.templateData || decryptedData.salt;
                        logger.log('✅ Function data extracted:', typeof functionData, functionData?.substring(0, 50) + '...');

                        if (!functionData) {
                            logger.error('❌ No function data found in tier2Data:', tier2Data);
                            return {
                                success: false,
                                error: 'No function data found in decrypted tier2'
                            };
                        }

                        return {
                            success: true,
                            data: functionData
                        };
                    } catch (decryptError) {
                        logger.error('❌ Failed to decrypt tier2 data:');
                        logger.error('Error type:', decryptError instanceof Error ? decryptError.constructor.name : typeof decryptError);
                        logger.error('Error message:', decryptError instanceof Error ? decryptError.message : String(decryptError));
                        logger.error('Error stack:', decryptError instanceof Error ? decryptError.stack?.split('\n').slice(0, 5).join('\n') : 'No stack');
                        logger.error('Tier2 data that failed:', {
                            encrypted: responseData.tier2.encrypted?.substring(0, 50) + '...',
                            iv: responseData.tier2.iv,
                            authTag: responseData.tier2.authTag
                        });

                        return {
                            success: false,
                            error: `Tier2 decryption failed: ${decryptError instanceof Error ? decryptError.message : String(decryptError)}`
                        };
                    }
                }

                // Fallback to old structure (no tier1/tier2)
                let functionData;
                switch (type) {
                    case 'workflow':
                        const workflowData = responseData;
                        functionData = workflowData.functionData || workflowData.templateData;
                        break;
                    case 'template':
                        const templateData = responseData;
                        functionData = templateData.functionData;
                        break;
                    case 'salt':
                        const saltData = responseData;
                        functionData = saltData.salt;
                        break;
                }

                return {
                    success: true,
                    data: functionData
                };
            } else {
                throw new Error(response.error || 'Retrieval failed');
            }

        } catch (error) {
            logger.error('❌ Failed to retrieve encoded data from backend:', error);

            // Try fallback from sessionStorage
            try {
                const fallbackKey = `backend_fallback_${id}`;
                const fallbackData = sessionStorage.getItem(fallbackKey);

                if (fallbackData) {
                    const parsed = JSON.parse(fallbackData);
                    logger.log('💾 Retrieved data from sessionStorage fallback');

                    return {
                        success: true,
                        data: parsed.data,
                        fallbackUsed: true
                    };
                }

                // Try to find in other sessionStorage keys
                const allKeys = Object.keys(sessionStorage);
                const matchingKey = allKeys.find(key => key.includes(id.split('_')[1]) || key.includes(id));

                if (matchingKey) {
                    const data = sessionStorage.getItem(matchingKey);
                    if (data) {
                        logger.log('💾 Found matching data in sessionStorage:', matchingKey);
                        const parsed = JSON.parse(data);

                        // Extract the actual function data, not the workflow ID
                        let functionData = parsed.functionData || parsed.data || parsed;

                        // If the functionData is still a workflow reference, we have a problem
                        if (typeof functionData === 'string' && this.isSecureReference(functionData)) {
                            logger.error('❌ SessionStorage contains workflow reference instead of function data:', functionData);
                            logger.log('🔍 Full sessionStorage data:', parsed);

                            // Try to find the actual hex data in the parsed object
                            const hexData = Object.values(parsed).find(value =>
                                typeof value === 'string' &&
                                value.startsWith('0x') &&
                                value.length > 10
                            );

                            if (hexData) {
                                logger.log('✅ Found hex data in sessionStorage:', hexData);
                                functionData = hexData;
                            } else {
                                logger.error('❌ No hex function data found in sessionStorage');
                                throw new Error('SessionStorage fallback contains reference instead of function data');
                            }
                        }

                        logger.log('✅ Returning function data from sessionStorage:', typeof functionData, functionData.substring(0, 50) + '...');

                        return {
                            success: true,
                            data: functionData,
                            fallbackUsed: true
                        };
                    }
                }

            } catch (fallbackError) {
                logger.error('❌ Fallback retrieval also failed:', fallbackError);
            }

            return {
                success: false,
                error: `Backend retrieval failed: HTTP 500: Internal Server Error`
            };
        }
    }

    /**
     * Auto-decrypt data if it's a secure reference
     */
    async autoDecryptIfNeeded(data: string): Promise<BackendDataResponse<string>> {
        try {
            logger.log('🔄 Auto-retrieving data from backend:', data);
            logger.log('🔍 Data type:', typeof data, 'Length:', data.length);

            // First, extract the reference from display format if it's wrapped in {{}}
            let cleanReference = this.extractReferenceFromDisplay(data);
            logger.log('🔧 Extracted reference from display format:', cleanReference);
            logger.log('🔍 Clean reference type:', typeof cleanReference, 'Length:', cleanReference.length);

            // Check if this is a secure reference
            if (!this.isSecureReference(cleanReference)) {
                logger.log('📋 Data is not a secure reference, returning as-is');
                return {
                    success: true,
                    data: data
                };
            }

            // Use the exact reference as the ID - no manipulation needed
            // The cleanReference should be something like "wf_step1_1755049208537_vyaknnl47"
            logger.log('📥 Retrieving workflow data from backend with exact ID:', cleanReference);

            // Try to retrieve the data using the exact ID
            const result = await this.retrieveEncodedData(cleanReference, 'workflow');

            if (result.success && result.data) {
                logger.log('✅ Successfully retrieved data from backend:', typeof result.data);

                // CRITICAL FIX: Extract hex function data from JSON if needed
                let hexFunctionData = result.data;

                // CLEAN IMPLEMENTATION: retrieveEncodedData already handles all decryption
                // The result.data is either:
                // 1. Plain hex string (from session re-encryption path)
                // 2. JSON string that needs parsing to extract hex

                if (typeof result.data === 'string') {
                    // Check if it's already hex data
                    if (result.data.startsWith('0x')) {
                        hexFunctionData = result.data;
                        logger.log('✅ Data is already hex format:', hexFunctionData.substring(0, 20) + '...');
                    } else {
                        // Try to parse as JSON to extract hex data
                        try {
                            const parsedData = JSON.parse(result.data);
                            logger.log('🔍 Parsed JSON data, extracting hex...');

                            // Extract hex from common field locations
                            if (parsedData.functionData && typeof parsedData.functionData === 'string' && parsedData.functionData.startsWith('0x')) {
                                hexFunctionData = parsedData.functionData;
                                logger.log('✅ Extracted hex from functionData:', hexFunctionData.substring(0, 20) + '...');
                            } else if (parsedData.data && typeof parsedData.data === 'string' && parsedData.data.startsWith('0x')) {
                                hexFunctionData = parsedData.data;
                                logger.log('✅ Extracted hex from data field:', hexFunctionData.substring(0, 20) + '...');
                            } else {
                                logger.error('❌ No hex data found in parsed JSON');
                                logger.error('Available fields:', Object.keys(parsedData));
                            }
                        } catch (parseError) {
                            logger.warn('⚠️ Data is not JSON and not hex format:', result.data.substring(0, 50) + '...');
                        }
                    }
                }

                // Validate that we have hex data
                if (typeof hexFunctionData === 'string' && hexFunctionData.startsWith('0x')) {
                    logger.log('✅ Successfully extracted hex function data:', hexFunctionData.substring(0, 20) + '...');
                    return {
                        success: true,
                        data: hexFunctionData
                    };
                } else {
                    logger.error('❌ Failed to extract valid hex function data. Type:', typeof hexFunctionData, 'Value preview:', String(hexFunctionData).substring(0, 100));
                    return {
                        success: false,
                        error: 'Retrieved data is not valid hex function data'
                    };
                }

            } else {
                logger.log('❌ Failed to retrieve workflow data:', result.error);
                return {
                    success: false,
                    error: result.error || 'Auto-retrieve failed'
                };
            }

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            logger.error('Auto-retrieve failed:', { originalData: data, error: errorMessage });

            return {
                success: false,
                error: `Auto-retrieve failed: ${errorMessage}`
            };
        }
    }

    /**
     * Health check for backend connectivity
     */
    async healthCheck(): Promise<boolean> {
        try {
            return await workflowStorageService.healthCheck();
        } catch (error) {
            logger.error('Backend health check failed:', error);
            return false;
        }
    }

    /**
     * Clear all fallback data from sessionStorage
     */
    clearFallbackData(): void {
        try {
            const keys = Object.keys(sessionStorage);
            const fallbackKeys = keys.filter(key => key.startsWith('backend_fallback_'));

            fallbackKeys.forEach(key => {
                sessionStorage.removeItem(key);
            });

            logger.log('🧹 Cleared fallback data from sessionStorage:', fallbackKeys.length, 'items');
        } catch (error) {
            logger.error('Failed to clear fallback data:', error);
        }
    }

    /**
     * Get display-friendly reference information
     */
    getReferenceInfo(reference: string): EncryptedDataReference | null {
        if (!this.isSecureReference(reference)) {
            return null;
        }

        const stepMatch = reference.match(/^workflow_(step\d+)_(\d+)_([a-zA-Z0-9]+)$/);
        if (stepMatch) {
            const [, stepId, timestamp, hash] = stepMatch;
            return {
                id: reference,
                type: 'workflow',
                isSecureReference: true,
                displayValue: this.formatReferenceForDisplay(reference)
            };
        }

        return {
            id: reference,
            type: 'workflow',
            isSecureReference: true,
            displayValue: this.formatReferenceForDisplay(reference)
        };
    }
}

// Export singleton instance
export const backendDataHandler = new BackendDataHandler();
export default backendDataHandler;
